import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  Button,
  Box,
  Typography,
  Avatar,
  CircularProgress,
  Grid,
  IconButton,
  Container,
  Alert,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  TextField,
  Select,
  FormControl,
  InputLabel,
} from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import SportsIcon from '@mui/icons-material/Sports';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import FlagOutlinedIcon from '@mui/icons-material/FlagOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useAuth } from '../contexts/AuthContext';
import { useApp, useOverridePath } from '../contexts/AppContext';
import { callCloudRun } from '../lib/cloudRun';
import { firebaseAuth } from '../lib/firebaseClient';
import { useNavigate } from 'react-router-dom';
import ImageCropModal from './ImageCropModal';
import CameraCaptureModal from './CameraCaptureModal';
import AmazingNetworkCard from './network/AmazingNetworkCard';
import { DISPLAY_FONT, PROFILE_COLORS, profileCardSx, SectionHeader } from './profile/profileUi';

interface RunnerProfile {
  email_id: string;
  runner_name: string;
  gender: string;
  address: string;
  zip: string;
  city: string;
  state: string;
  country: string;
  phone_no: string;
  dob: string;
  profile_picture: string | null;
}

interface TimelineEntry {
  season: string;
  race_distance: string | null;
  coach: string | null;
  coach_picture: string | null;
  coach_profile_url: string | null;
  coach_phone: string | null;
}

// Helper function to get initials from a name
const getInitials = (name: string | null): string => {
  if (!name) return '?';
  const words = name.trim().split(/\s+/);
  if (words.length === 1) {
    return words[0].charAt(0).toUpperCase();
  }
  return (words[0].charAt(0) + words[words.length - 1].charAt(0)).toUpperCase();
};

const C = PROFILE_COLORS;

const chipSx = {
  display: 'flex',
  alignItems: 'center',
  gap: 1,
  height: 32,
  px: 1.75,
  borderRadius: 999,
  fontSize: 13,
  fontWeight: 600,
} as const;

const DetailItem: React.FC<{ icon: React.ReactNode; label: string; value: React.ReactNode; sx?: SxProps<Theme> }> = ({ icon, label, value, sx }) => (
  <Box sx={[{ display: 'flex', alignItems: 'flex-start', gap: 1.5, minWidth: 0 }, ...(Array.isArray(sx) ? sx : [sx])]}>
    <Box sx={{ width: 36, height: 36, borderRadius: '10px', bgcolor: C.subtle, color: C.muted, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, '& svg': { fontSize: 19 } }}>
      {icon}
    </Box>
    <Box sx={{ minWidth: 0 }}>
      <Typography sx={{ fontSize: 12, color: C.muted }}>{label}</Typography>
      <Typography component="div" sx={{ fontSize: 15, fontWeight: 600, color: C.ink, lineHeight: 1.45, overflowWrap: 'anywhere' }}>
        {value}
      </Typography>
    </Box>
  </Box>
);

/** Faint running-track lanes in the header's top-right corner. */
const TrackLanes: React.FC = () => (
  <Box
    component="svg"
    viewBox="0 0 560 380"
    aria-hidden="true"
    sx={{ position: 'absolute', width: { xs: 360, md: 560 }, right: { xs: -150, md: -120 }, top: { xs: -110, md: -170 }, pointerEvents: 'none' }}
  >
    <rect x="10" y="10" width="540" height="360" rx="180" fill="#F5F8FE" stroke="#DCE7FA" strokeWidth="1.5" />
    <rect x="32" y="32" width="496" height="316" rx="158" fill="none" stroke="#DCE7FA" strokeWidth="1.5" />
    <rect x="54" y="54" width="452" height="272" rx="136" fill="none" stroke="#DCE7FA" strokeWidth="1.5" />
    <rect x="76" y="76" width="408" height="228" rx="114" fill="none" stroke="#DCE7FA" strokeWidth="1.5" />
    <rect x="98" y="98" width="364" height="184" rx="92" fill="#FFFFFF" stroke="#DCE7FA" strokeWidth="1.5" />
  </Box>
);

// race_distance is free text ("10K", "10k", "4a. Full Marathon (Beginner)",
// "L2"…), so colour by pattern and fall back to grey.
const distanceColors = (distance: string): { bg: string; fg: string } => {
  if (/full|ultra|\bum\b/i.test(distance)) return { bg: '#F1EAFE', fg: '#5B21B6' };
  if (/half/i.test(distance)) return { bg: C.blueTint, fg: C.blueText };
  if (/10\s*k/i.test(distance)) return { bg: '#E3F6F3', fg: '#0F766E' };
  if (/\b[58]\s*k/i.test(distance)) return { bg: '#FEF3E2', fg: '#92400E' };
  return { bg: C.subtle, fg: C.body };
};

const WHATSAPP_ICON =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Cpath fill='%2325D366' d='M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z'/%3E%3C/svg%3E";

/** One season as a race bib: season number, distance, coach. */
const SeasonBib: React.FC<{ entry: TimelineEntry; latest: boolean }> = ({ entry, latest }) => {
  const seasonNo = entry.season.match(/\d+/)?.[0];
  const pill = latest ? { bg: 'white', fg: C.blueText } : distanceColors(entry.race_distance ?? '');
  const fg = latest ? 'white' : C.ink;
  const soft = latest ? 'rgba(255,255,255,0.85)' : C.muted;
  const actionSx = {
    width: 32,
    height: 32,
    flexShrink: 0,
    bgcolor: latest ? 'rgba(255,255,255,0.14)' : C.subtle,
    color: latest ? 'white' : C.body,
    '&:hover': { bgcolor: latest ? 'rgba(255,255,255,0.24)' : '#E8EBF0' },
  };
  const hole = latest ? '#0A4596' : C.border;

  return (
    <Box
      sx={{
        position: 'relative',
        gridColumn: latest ? { xs: 'span 2', md: 'auto' } : undefined,
        borderRadius: '16px',
        border: latest ? 'none' : `1px solid ${C.border}`,
        bgcolor: latest ? C.blueDark : 'white',
        color: fg,
        p: { xs: '14px 14px 12px', md: '18px 18px 14px' },
        display: 'flex',
        flexDirection: 'column',
        gap: 1.25,
        minWidth: 0,
      }}
    >
      {/* Safety-pin holes */}
      {[{ left: 8, top: 8 }, { right: 8, top: 8 }, { left: 8, bottom: 8 }, { right: 8, bottom: 8 }].map((pos, i) => (
        <Box key={i} sx={{ position: 'absolute', ...pos, width: 6, height: 6, borderRadius: '50%', bgcolor: hole }} />
      ))}

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, minWidth: 0 }}>
        {/* Narrow phone bibs drop the labels so the distance tag fits */}
        <Typography sx={{ display: { xs: latest ? 'block' : 'none', md: 'block' }, fontSize: 11, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', color: soft, whiteSpace: 'nowrap' }}>
          {latest ? 'Latest' : 'Season'}
        </Typography>
        {entry.race_distance && (
          <Box sx={{ height: 24, px: { xs: 1, md: 1.25 }, borderRadius: 999, bgcolor: pill.bg, color: pill.fg, fontSize: { xs: 11, md: 12 }, fontWeight: 700, display: 'flex', alignItems: 'center', minWidth: 0 }}>
            <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{entry.race_distance}</Box>
          </Box>
        )}
      </Box>

      <Typography sx={{ fontFamily: DISPLAY_FONT, fontStretch: '75%', fontWeight: 900, fontSize: seasonNo ? { xs: 48, md: 64 } : 24, lineHeight: 0.95, color: fg }}>
        {seasonNo ?? entry.season}
      </Typography>

      <Box sx={{ borderTop: `1.5px dashed ${latest ? 'rgba(255,255,255,0.35)' : C.border}` }} />

      {/* Coach: photo, label and links on one line, the full name under it */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
        <Avatar
          src={entry.coach_picture || undefined}
          sx={{ width: 32, height: 32, fontSize: 12, fontWeight: 700, bgcolor: latest ? 'rgba(255,255,255,0.18)' : '#EEF1F5', color: latest ? 'white' : C.body }}
        >
          {entry.coach ? getInitials(entry.coach) : <SportsIcon sx={{ fontSize: 16 }} />}
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ display: { xs: latest ? 'block' : 'none', md: 'block' }, fontSize: 11, color: soft }}>Coach</Typography>
        </Box>
        {entry.coach && entry.coach_phone && (
          <IconButton
            component="a"
            href={`https://wa.me/${entry.coach_phone.replace(/\D/g, '')}`}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`WhatsApp ${entry.coach}`}
            sx={actionSx}
          >
            <Box component="img" src={WHATSAPP_ICON} alt="" sx={{ width: 16, height: 16 }} />
          </IconButton>
        )}
        {entry.coach && entry.coach_profile_url && (
          <IconButton
            component="a"
            href={entry.coach_profile_url}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`${entry.coach}'s coach profile`}
            sx={actionSx}
          >
            <OpenInNewIcon sx={{ fontSize: 16 }} />
          </IconButton>
        )}
      </Box>
      <Typography sx={{ mt: -0.5, fontSize: 14, fontWeight: 700, lineHeight: 1.3, color: fg, overflowWrap: 'anywhere' }}>
        {entry.coach || 'Not assigned'}
      </Typography>
    </Box>
  );
};


const UserProfile: React.FC = () => {
  const { user } = useAuth();
  const { effectiveEmail, isOverrideActive } = useApp();
  const navigate = useNavigate();
  const withOverride = useOverridePath();
  const [profileData, setProfileData] = useState<RunnerProfile | null>(null);
  const [timelineData, setTimelineData] = useState<TimelineEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [showCropModal, setShowCropModal] = useState(false);
  const [showCameraModal, setShowCameraModal] = useState(false);
  const [selectedImageSrc, setSelectedImageSrc] = useState<string>('');
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Edit mode state
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState<Partial<RunnerProfile>>({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Handle avatar click to show photo source menu
  const handleAvatarClick = (event: React.MouseEvent<HTMLElement>) => {
    if (!isOverrideActive && !uploading) {
      setMenuAnchorEl(event.currentTarget);
    }
  };

  // Close the photo source menu
  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  // Handle selecting "Upload Photo" from menu
  const handleUploadOption = () => {
    handleMenuClose();
    fileInputRef.current?.click();
  };

  // Handle selecting "Take Photo" from menu
  const handleCameraOption = () => {
    handleMenuClose();
    setShowCameraModal(true);
  };

  // Handle camera capture - opens crop modal with captured image
  const handleCameraCapture = (imageSrc: string) => {
    setShowCameraModal(false);
    setSelectedImageSrc(imageSrc);
    setShowCropModal(true);
  };

  // Use effectiveEmail (which respects override) for profile data
  const targetEmail = effectiveEmail || user?.email || '';
  // The email the page is showing now. In override mode the signed-in user's
  // own profile is requested first, then the override's; without this check
  // whichever response lands last wins, so an admin could see their own
  // details under someone else's name.
  const currentEmailRef = useRef(targetEmail);
  currentEmailRef.current = targetEmail;

  const fetchProfileData = useCallback(async () => {
    try {
      setLoading(true);
      const res = await callCloudRun<{ profile: RunnerProfile & { display_name?: string } }>(
        '/api/profile', { email: targetEmail }
      );
      if (currentEmailRef.current !== targetEmail) return;
      const p = res.profile;
      setProfileData({
        email_id: p.email_id || targetEmail,
        runner_name: p.runner_name || '',
        gender: p.gender || '',
        address: p.address || '',
        zip: p.zip || '',
        city: p.city || '',
        state: p.state || '',
        country: p.country || '',
        phone_no: p.phone_no || '',
        dob: p.dob || '',
        profile_picture: p.profile_picture || null,
      });
    } catch (err) {
      console.error('Error loading profile:', err);
    } finally {
      setLoading(false);
    }
  }, [targetEmail]);

  const fetchTimelineData = useCallback(async () => {
    try {
      const res = await callCloudRun<{ timeline: TimelineEntry[] }>(
        '/api/profile/timeline', { email: targetEmail }
      );
      if (currentEmailRef.current !== targetEmail) return;
      setTimelineData(res.timeline || []);
    } catch (err) {
      console.error('Error loading timeline:', err);
    }
  }, [targetEmail]);

  useEffect(() => {
    // Use targetEmail which respects override mode
    if (targetEmail) {
      fetchProfileData();
      fetchTimelineData();
    }
  }, [targetEmail, fetchProfileData, fetchTimelineData]);

  // Handle file selection - validates and opens crop modal
  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !user?.email) return;

    // Reset file input so the same file can be selected again
    event.target.value = '';

    // Block avatar upload in override mode (admin viewing another user's profile)
    if (isOverrideActive) {
      alert('Cannot modify profile picture when viewing another user\'s profile');
      return;
    }

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Please upload an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('File size must be less than 5MB');
      return;
    }

    // Read file as data URL and open crop modal
    const reader = new FileReader();
    reader.onload = () => {
      setSelectedImageSrc(reader.result as string);
      setShowCropModal(true);
    };
    reader.readAsDataURL(file);
  };

  // Handle crop complete - uploads cropped image via Cloud Run
  const handleCropComplete = async (croppedBlob: Blob) => {
    if (!user?.email) return;

    try {
      setUploading(true);
      setShowCropModal(false);

      // Upload to GCS via Cloud Run. The server picks the storage path from the
      // verified token.
      const fbUser = firebaseAuth.currentUser;
      if (!fbUser) {
        alert('Session expired. Please log in again.');
        return;
      }
      const idToken = await fbUser.getIdToken();

      const cloudRunUrl = process.env.REACT_APP_CLOUD_RUN_URL;
      if (!cloudRunUrl) {
        console.error('REACT_APP_CLOUD_RUN_URL not set');
        alert('Upload service not configured. Please contact support.');
        return;
      }

      const formData = new FormData();
      formData.append('file', croppedBlob, 'avatar.jpg');

      const uploadRes = await fetch(`${cloudRunUrl}/upload-profile-picture`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${idToken}` },
        body: formData,
      });

      if (!uploadRes.ok) {
        const err = await uploadRes.json().catch(() => ({}));
        console.error('Upload error:', err);
        alert('Failed to upload image. Please try again.');
        return;
      }

      const { url: publicUrl } = await uploadRes.json();

      console.log('Public URL:', publicUrl);

      // Update profile picture URL via Cloud Run
      const updateRes = await callCloudRun('/api/profile/picture', { url: publicUrl });
      if (!(updateRes as any).ok) {
        alert('Failed to update profile. Please try again.');
        return;
      }

      // Refresh profile data to show new avatar
      await fetchProfileData();

      console.log('Profile refreshed. New profile_picture:', profileData?.profile_picture);
    } catch (err) {
      console.error('Error uploading avatar:', err);
      alert('An unexpected error occurred. Please try again.');
    } finally {
      setUploading(false);
      setSelectedImageSrc('');
    }
  };

  const startEditing = () => {
    if (!profileData) return;
    setEditForm({
      runner_name: profileData.runner_name,
      gender: profileData.gender,
      phone_no: profileData.phone_no,
      dob: profileData.dob ? profileData.dob.split('T')[0] : '',
      address: profileData.address,
      city: profileData.city,
      state: profileData.state,
      zip: profileData.zip,
      country: profileData.country,
    });
    setSaveError(null);
    setIsEditing(true);
  };

  const cancelEditing = () => {
    setIsEditing(false);
    setEditForm({});
    setSaveError(null);
  };

  const saveProfile = async () => {
    setSaving(true);
    setSaveError(null);
    try {
      await callCloudRun('/api/profile/update', {
        email: targetEmail,
        ...editForm,
      });
      await fetchProfileData();
      setIsEditing(false);
      setEditForm({});
    } catch (err: any) {
      setSaveError(err?.message || 'Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (dateString: string) => {
    // Parse as local date to avoid UTC-to-local timezone shift on date-only strings
    const [year, month, day] = dateString.split('T')[0].split('-').map(Number);
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  const canEdit = !isOverrideActive;
  const latest = timelineData[0] ?? null;
  const firstSeason = timelineData[timelineData.length - 1] ?? null;
  const coachCount = new Set(timelineData.map((t) => t.coach).filter(Boolean)).size;
  const displayName = profileData?.runner_name || user?.name || 'Runner';
  const addressLine = [
    profileData?.address,
    [profileData?.city, profileData?.state, profileData?.zip].filter(Boolean).join(', '),
    profileData?.country,
  ].filter(Boolean).join(', ');

  // An empty field is a prompt to fill it in for the runner themselves, plain
  // text when an admin is looking.
  const emptyValue = canEdit ? (
    <Box
      component="button"
      onClick={startEditing}
      sx={{ p: 0, border: 0, bgcolor: 'transparent', font: 'inherit', fontSize: 15, fontWeight: 700, color: C.blueDark, cursor: 'pointer', textAlign: 'left' }}
    >
      + Add
    </Box>
  ) : (
    'Not provided'
  );

  const editButton = (
    <Button
      onClick={startEditing}
      variant="outlined"
      startIcon={<EditOutlinedIcon />}
      sx={{
        position: 'relative',
        height: 40,
        px: 2,
        borderRadius: 999,
        borderColor: '#D5D9DF',
        color: C.ink,
        fontWeight: 700,
        textTransform: 'none',
        bgcolor: 'white',
        '&:hover': { borderColor: C.blue, bgcolor: 'white' },
      }}
    >
      Edit profile
    </Button>
  );

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: C.page, color: C.ink }}>
      {/* Top bar */}
      <Box sx={{ height: 64, display: 'flex', alignItems: 'center', gap: 1.5, px: { xs: 2, md: 10 }, bgcolor: 'white', borderBottom: `1px solid ${C.border}` }}>
        <Button
          onClick={() => navigate(withOverride('/'))}
          startIcon={<ArrowBackIcon />}
          sx={{ height: 40, px: 1.75, borderRadius: 999, bgcolor: C.subtle, color: C.body, fontWeight: 600, textTransform: 'none', '&:hover': { bgcolor: '#E8EBF0' } }}
        >
          Dashboard
        </Button>
        <Typography sx={{ color: '#A0A6B1' }}>/</Typography>
        <Typography sx={{ fontWeight: 600, fontSize: 14 }}>Profile</Typography>
      </Box>

      <Container maxWidth="lg" sx={{ py: { xs: 2, md: 4 }, display: 'flex', flexDirection: 'column', gap: { xs: 2, md: 3 } }}>
        {/* Header: who you are, plus your details */}
        <Box component="section" sx={{ ...profileCardSx, position: 'relative', overflow: 'hidden', display: 'flex', flexDirection: 'column', gap: 3 }}>
          <TrackLanes />

          <Box sx={{ position: 'relative', display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' }, gap: { xs: 2.5, md: 3.5 } }}>
            {/* Avatar with Upload */}
            <Box sx={{ position: 'relative', flexShrink: 0 }}>
              <input
                accept="image/*"
                style={{ display: 'none' }}
                ref={fileInputRef}
                type="file"
                onChange={handleFileSelect}
                disabled={uploading}
              />
              <Avatar
                src={profileData?.profile_picture || undefined}
                onClick={handleAvatarClick}
                sx={{
                  width: { xs: 96, md: 120 },
                  height: { xs: 96, md: 120 },
                  bgcolor: C.blueTint,
                  color: C.blueDark,
                  fontFamily: DISPLAY_FONT,
                  fontWeight: 800,
                  fontSize: { xs: 34, md: 42 },
                  boxShadow: `0 0 0 4px white, 0 0 0 5px ${C.border}`,
                  cursor: canEdit ? 'pointer' : 'default',
                }}
              >
                {!profileData?.profile_picture && getInitials(displayName)}
              </Avatar>
              {canEdit && (
                <IconButton
                  onClick={handleAvatarClick}
                  disabled={uploading}
                  aria-label="Change photo"
                  sx={{
                    position: 'absolute',
                    bottom: -4,
                    right: -4,
                    width: 44,
                    height: 44,
                    bgcolor: C.blue,
                    color: 'white',
                    border: '3px solid white',
                    '&:hover': { bgcolor: C.blueDark },
                  }}
                >
                  {uploading ? <CircularProgress size={16} sx={{ color: 'white' }} /> : <CameraAltIcon sx={{ fontSize: 18 }} />}
                </IconButton>
              )}
            </Box>

            <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 1.25 }}>
              {firstSeason && (
                <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: C.muted }}>
                  With RHWB since {firstSeason.season}
                </Typography>
              )}
              <Typography
                component="h1"
                sx={{ fontFamily: DISPLAY_FONT, fontStretch: '88%', fontWeight: 800, fontSize: { xs: 34, md: 48 }, lineHeight: 1, letterSpacing: '-0.01em', overflowWrap: 'anywhere' }}
              >
                {displayName}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, color: C.muted, minWidth: 0 }}>
                <MailOutlineIcon sx={{ fontSize: 17 }} />
                <Typography sx={{ fontSize: 15, color: 'inherit' }} noWrap>
                  {profileData?.email_id || user?.email}
                </Typography>
              </Box>
              {latest && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 0.5 }}>
                  <Box sx={{ ...chipSx, bgcolor: C.blueTint, color: C.blueText, fontWeight: 700 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: C.blue }} />
                    {[latest.season, latest.race_distance].filter(Boolean).join(' · ')}
                  </Box>
                  {latest.coach && (
                    <Box sx={{ ...chipSx, pl: 0.5, bgcolor: C.subtle, color: C.body }}>
                      <Avatar src={latest.coach_picture || undefined} sx={{ width: 24, height: 24, fontSize: 10, fontWeight: 700, bgcolor: 'white', color: C.body, border: `1px solid ${C.border}` }}>
                        {getInitials(latest.coach)}
                      </Avatar>
                      Coached by {latest.coach}
                    </Box>
                  )}
                </Box>
              )}
            </Box>

            {canEdit && !isEditing && (
              <Box sx={{ display: { xs: 'none', sm: 'flex' }, alignSelf: 'flex-start' }}>{editButton}</Box>
            )}
          </Box>

          {saveError && (
            <Alert severity="error" sx={{ position: 'relative' }}>{saveError}</Alert>
          )}

          <Box sx={{ position: 'relative', pt: 3, borderTop: `1px solid ${C.divider}` }}>
            {isEditing ? (
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Full Name" fullWidth size="small"
                    value={editForm.runner_name || ''}
                    onChange={e => setEditForm(f => ({ ...f, runner_name: e.target.value }))}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Gender</InputLabel>
                    <Select
                      label="Gender"
                      value={editForm.gender || ''}
                      onChange={e => setEditForm(f => ({ ...f, gender: e.target.value as string }))}
                    >
                      <MenuItem value="Male">Male</MenuItem>
                      <MenuItem value="Female">Female</MenuItem>
                      <MenuItem value="Other">Other</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Phone Number" fullWidth size="small"
                    value={editForm.phone_no || ''}
                    onChange={e => setEditForm(f => ({ ...f, phone_no: e.target.value }))}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Date of Birth" type="date" fullWidth size="small"
                    InputLabelProps={{ shrink: true }}
                    value={editForm.dob || ''}
                    onChange={e => setEditForm(f => ({ ...f, dob: e.target.value }))}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Street Address" fullWidth size="small"
                    value={editForm.address || ''}
                    onChange={e => setEditForm(f => ({ ...f, address: e.target.value }))}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="City" fullWidth size="small"
                    value={editForm.city || ''}
                    onChange={e => setEditForm(f => ({ ...f, city: e.target.value }))}
                  />
                </Grid>
                <Grid item xs={6} sm={4}>
                  <TextField
                    label="State" fullWidth size="small"
                    value={editForm.state || ''}
                    onChange={e => setEditForm(f => ({ ...f, state: e.target.value }))}
                  />
                </Grid>
                <Grid item xs={6} sm={4}>
                  <TextField
                    label="ZIP" fullWidth size="small"
                    value={editForm.zip || ''}
                    onChange={e => setEditForm(f => ({ ...f, zip: e.target.value }))}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Country" fullWidth size="small"
                    value={editForm.country || ''}
                    onChange={e => setEditForm(f => ({ ...f, country: e.target.value }))}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', gap: 1.25, justifyContent: 'flex-end', mt: 1 }}>
                    <Button
                      onClick={cancelEditing} disabled={saving} variant="outlined"
                      sx={{ height: 44, px: 2.5, borderRadius: 999, textTransform: 'none', fontWeight: 700, color: C.ink, borderColor: '#D5D9DF' }}
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={saveProfile} disabled={saving} variant="contained" disableElevation
                      startIcon={saving ? <CircularProgress size={14} sx={{ color: 'white' }} /> : undefined}
                      sx={{ height: 44, px: 2.75, borderRadius: 999, textTransform: 'none', fontWeight: 700, bgcolor: C.blueDark, '&:hover': { bgcolor: '#0A4596' } }}
                    >
                      {saving ? 'Saving…' : 'Save changes'}
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            ) : (
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', md: 'repeat(5, minmax(0, 1fr))' }, gap: { xs: 2, md: 3 } }}>
                <DetailItem icon={<PersonOutlineIcon />} label="Gender" value={profileData?.gender || emptyValue} />
                <DetailItem
                  icon={<CalendarMonthOutlinedIcon />}
                  label="Birthday"
                  value={profileData?.dob ? formatDate(profileData.dob) : emptyValue}
                />
                <DetailItem icon={<PhoneOutlinedIcon />} label="Phone" value={profileData?.phone_no || emptyValue} />
                <DetailItem
                  icon={<PlaceOutlinedIcon />}
                  label="Address"
                  value={addressLine || emptyValue}
                  sx={{ gridColumn: { sm: 'span 2', md: 'span 2' } }}
                />
              </Box>
            )}
          </Box>

          {canEdit && !isEditing && (
            <Box sx={{ display: { xs: 'flex', sm: 'none' }, position: 'relative', '& > button': { flex: 1, height: 44 } }}>{editButton}</Box>
          )}
        </Box>

        {/* Who brought you to RHWB, and who you brought. In override mode an
            admin sees the viewed runner's network, read-only. */}
        <AmazingNetworkCard viewEmail={isOverrideActive ? targetEmail : null} />

        {/* RHWB Timeline: one race bib per season, newest first */}
        <Box component="section" sx={{ ...profileCardSx, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          <SectionHeader
            icon={<FlagOutlinedIcon />}
            tint={C.blueTint}
            color={C.blueDark}
            title="RHWB Timeline"
            subtitle={
              timelineData.length > 0
                ? [
                    `${timelineData.length} ${timelineData.length === 1 ? 'season' : 'seasons'}`,
                    coachCount > 0 ? `${coachCount} ${coachCount === 1 ? 'coach' : 'coaches'}` : null,
                  ].filter(Boolean).join(' · ')
                : undefined
            }
          />

          {timelineData.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4, color: C.muted }}>
              <FlagOutlinedIcon sx={{ fontSize: '3rem', mb: 1, opacity: 0.3 }} />
              <Typography variant="body2">No season history available</Typography>
            </Box>
          ) : (
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', md: 'repeat(4, minmax(0, 1fr))' }, gap: { xs: 1.25, md: 2 } }}>
              {timelineData.map((entry, index) => (
                <SeasonBib key={`${entry.season}-${index}`} entry={entry} latest={index === 0} />
              ))}
            </Box>
          )}
        </Box>
      </Container>

      {/* Photo Source Menu */}
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        PaperProps={{
          sx: {
            borderRadius: 2,
            mt: 1,
            minWidth: 180,
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
          }
        }}
      >
        <MenuItem onClick={handleCameraOption} sx={{ py: 1.5 }}>
          <ListItemIcon>
            <CameraAltIcon sx={{ color: '#1877F2' }} />
          </ListItemIcon>
          <ListItemText primary="Take Photo" />
        </MenuItem>
        <MenuItem onClick={handleUploadOption} sx={{ py: 1.5 }}>
          <ListItemIcon>
            <PhotoLibraryIcon sx={{ color: '#1877F2' }} />
          </ListItemIcon>
          <ListItemText primary="Upload Photo" />
        </MenuItem>
      </Menu>

      {/* Camera Capture Modal */}
      <CameraCaptureModal
        open={showCameraModal}
        onClose={() => setShowCameraModal(false)}
        onCapture={handleCameraCapture}
      />

      {/* Image Crop Modal */}
      <ImageCropModal
        open={showCropModal}
        imageSrc={selectedImageSrc}
        onClose={() => {
          setShowCropModal(false);
          setSelectedImageSrc('');
        }}
        onCropComplete={handleCropComplete}
      />
    </Box>
  );
};

export default UserProfile;
